import React, {useState, useEffect}  from "react";
import {useGlobalStatus} from '../../services/GlobalStatusProvider';
import { addDays, format, getWeek, endOfWeek, isWithinInterval } from "date-fns";
import { 
  Body1Stronger,
  Subtitle2Stronger,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  mergeClasses,
  Table, 
  TableHeader, 
  TableHeaderCell, 
  TableBody, 
  TableRow, 
  TableCell} from "@fluentui/react-components";
import WeekRow from "./WeekRow";
import EntryCellEditor from "./EntryCellEditor";
import EntryCell from "./EntryCell";
import DayCombined from "./DayCombined";
import Totals from "./Totals";
import Leave from "./Leave"
import TooltipWatcherWidget from "../../components/TooltipWatcherWidget";

export default function WeekView(props) {
  const [dayCombinedOpen, setdayCombinedOpen]= useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayCombinedEntries, setDayCombinedEntries] = useState(null);

  const [entryDialogOpen, setEntryDialogOpen]= useState(false);
  const [entryData, setEntryData] = useState({});
  const [entryDay, setEntryDay] = useState({});
  const{currentWeekStart, isDesktop, useStyles }= useGlobalStatus();
  const classes = useStyles();
  const [suggestedStartDict, setSuggestedStartDict] = useState({});

  const handleEntryCellAction= (data, day)=>{
    setEntryData(data);
    setEntryDay(day);
    setEntryDialogOpen(true);
  }

  const handleUpdateWeekView=(data,day) => {
    props.handleEntryCellAction(data,day);
  }

  const handleDialogClose=()=>{
    setEntryDialogOpen(false);
    setdayCombinedOpen(false);
  };

  const handleSetDayCombinedEntries = (date) => {
    const entries = []
    props.timesheetData.forEach(client => {
        client.projects.forEach(project => {
            project.budgets.forEach(budget => {
                budget.timecodes.forEach(timecode => {
                    timecode.entryDays.forEach(day => {
                        if (day.date.getTime() === date.getTime() && day.status !== "empty") {
                          day.entries.forEach(entry => {
                            entries.push([entry, project, timecode]);
                          });
                        }
                    });
                });
            });
        });
    });
    entries.sort((a, b) => a[0].start.localeCompare(b[0].start));
    setDayCombinedEntries(entries);
  }

  useEffect(() => {
    const suggestedStartDictTemp = {}
    for(var i=0; i<7; i++){
      suggestedStartDictTemp[format(addDays(currentWeekStart,i),'dd/MM/yyyy')] = "00:00";
    }
    setSuggestedStartDict(suggestedStartDictTemp);
  }, [currentWeekStart]);
  
  const dates=[];
  const weekDays=["Mon","Tues","Wed","Thur","Fri","Sat","Sun"]
  for(var i=0; i<7; i++){
    var day = addDays(currentWeekStart,i);
    dates.push([format(day,'do, LLL'), weekDays[i], day]);
  }

  const calculateTotal = (data, day) => {
        var exclude;
        if(day==null) exclude = new Date(1900,1,1);
        else exclude = day.date;

        var total = 0;
        
        var validDays = data.entryDays.filter(q=> q.status!=='empty' && q.status!=='error' && q.date!== exclude && 
            isWithinInterval(q.date, {start: currentWeekStart, end: addDays(endOfWeek(currentWeekStart),1)}));
        total = (validDays.reduce( function(total, item){                
                return total + (
                    item.entries.reduce(function(dayTotal, entry){
                        return dayTotal + entry.totalMinutes;
                    },0));
            }, 
        0));     
        return (Math.round(total / 60 * 100) / 100).toFixed(2)+"hr";
    }
 
  return (
    <>
      {isDesktop? (
        <>
        <Table className="week-view-table">
          <TableHeader>
          <TableRow>
          <TableHeaderCell style={{width: '200px', fontWeight: "bold"}}>Project</TableHeaderCell>
          <TableHeaderCell style={{fontWeight: "bold" }}>Timecode</TableHeaderCell>
        {dates.map((instance, index) => (
          (instance[1] === "Thur") ? 
          <TableHeaderCell className="week-view-table-days" onClick={()=> {setSelectedDate(instance[2]); handleSetDayCombinedEntries(instance[2]); setdayCombinedOpen(true);}}  key={index}>
            <TooltipWatcherWidget text="Click to see all entries for a day, providing a unified location to view, edit and delete entries" direction="above-end">
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", textAlign: "center", width: '100%', padding: '0.25rem'}} >
                <div style={{fontWeight: "bold"}}>{instance[1]}</div>
                <div>{instance[0]}</div>
              </div>
              </TooltipWatcherWidget>
          </TableHeaderCell>
          :
          <TableHeaderCell className="week-view-table-days" onClick={()=> {setSelectedDate(instance[2]); handleSetDayCombinedEntries(instance[2]); setdayCombinedOpen(true);}} key={index}>
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", textAlign: "center", width: '100%', padding: '0.25rem'}} >
                <div style={{fontWeight: "bold"}}>{instance[1]}</div>
                <div>{instance[0]}</div>
              </div>
          </TableHeaderCell>
        ))}
        <TableHeaderCell>
          <TooltipWatcherWidget text="Total hours for the week for each timecode or leave" direction="above-end">
            <Body1Stronger style={{display: 'flex', justifyContent: "center", width: '100%'}}>Total</Body1Stronger>
          </TooltipWatcherWidget>
        </TableHeaderCell>
        </TableRow>
        </TableHeader>
        <TableBody>
        {props.timesheetData.map((client) => (
          <React.Fragment key={`c${client.clientId}`}>
            {client.projects.map((project) => {
              const timecodeRows = [];

              project.budgets.forEach((budget) => {
                budget.timecodes.forEach((timecode) => {
                  timecodeRows.push({ timecode, budget });
                });
              });

              if (timecodeRows.length === 0) return null;

              return timecodeRows.map(({ timecode, budget }, index) => (
                <TableRow key={`c${client.clientId}-p${project.projectId}-b${budget.budgetId}-t${timecode.taskId}-${index}`}>
                  {/* Show project name only on the first row */}
                  {index === 0 ? (
                    <TableHeaderCell rowSpan={timecodeRows.length}>
                      <Subtitle2Stronger>{project.projectName}</Subtitle2Stronger>
                    </TableHeaderCell>
                  ) : null}

                  {/* Timecode name */}
                  <TableCell style={{backgroundColor: '#eff1f5'}}>
                    {timecode.taskName}
                  </TableCell>

                  {/* Week view cells */}
                  <WeekRow
                    data={timecode}
                    keybase={`c${client.clientId}-p${project.projectId}-b${budget.budgetId}-t${timecode.taskId}-w${getWeek(currentWeekStart)}`}
                    handleEntryCellAction={handleEntryCellAction}
                  />
                </TableRow>
              ));
            })}
          </React.Fragment>
        ))}
        <Leave leaveData={props.leaveData} />


      <TableRow>
        <Totals timesheetData={props.timesheetData} leaveData={props.leaveData}/>
      </TableRow>

      </TableBody>
  
      {/* Entry Cell Editor */}
      <EntryCellEditor
        timesheetData={props.timesheetData}
        dialogOpen={entryDialogOpen}
        entryData={entryData}
        entryDay={entryDay}
        setEntryDay={setEntryDay}
        handleDialogClose={handleDialogClose}
        handleUpdateWeekView={handleUpdateWeekView}
        suggestedStartDict={suggestedStartDict}
        setSuggestedStartDict={setSuggestedStartDict}
      />
      <DayCombined
        timesheetData={props.timesheetData}
        dialogOpen={dayCombinedOpen}
        date={selectedDate}
        entries={dayCombinedEntries}
        handleDialogClose={handleDialogClose}
        handleUpdateWeekView={handleUpdateWeekView}
      />
      
      </Table>
      
      </>
      ) : (
        <Accordion collapsible>
          {props.timesheetData.map((client, clientIndex) =>
            client.projects
              .map((project, projectIndex) => (
                project.budgets
                .map((budget, budgetIndex) => (
                  budget.timecodes
                .map((timecode, timecodeIndex) => (
                  <AccordionItem  
                  key={`acc-item-${client.clientId}-${project.projectId}-${budget.budgetId}-${timecode.taskId}`}
                  value={`${clientIndex}-${projectIndex}-${budgetIndex}-${timecodeIndex}`}>
                    <AccordionHeader className={mergeClasses(classes.accordionHeader)}>{project.projectName} - {timecode.taskName} - {calculateTotal(timecode)}</AccordionHeader>
                    <AccordionPanel className={mergeClasses(classes.accordionPanel)}>
                      {timecode.entryDays.map((day) => (
                          <EntryCell 
                          key={`entry-${clientIndex}-${projectIndex}-${budgetIndex}-${timecodeIndex}-${day.date}`}
                          handleEntryCellAction={handleEntryCellAction} 
                          data={day}
                          timecodeData={timecode}
                          />
                      ))}
                    </AccordionPanel>
                  </AccordionItem>
                ))
              ))    
            ))
          )}
          <Leave leaveData={props.leaveData}></Leave>
        </Accordion>
      )}
      {/* Entry Cell Editor */}
      <EntryCellEditor
        timesheetData={props.timesheetData}
        dialogOpen={entryDialogOpen}
        entryData={entryData}
        entryDay={entryDay}
        setEntryDay={setEntryDay}
        handleDialogClose={handleDialogClose}
        handleUpdateWeekView={handleUpdateWeekView}
        suggestedStartDict={suggestedStartDict}
        setSuggestedStartDict={setSuggestedStartDict}
      />
    </>
  );
}